import React from "react";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Check } from 'lucide-react';
import folderIcon from '../../../../assets/images/newFolderIcon.svg';
import { ReactComponent as ChevronRight } from '../../../../assets/images/arrow-down-blue.svg';
import { ReactComponent as FileIcon } from '../../../../assets/images/file.svg';
import { ReactComponent as DownloadIcon } from '../../../../assets/images/download-black.svg';
import { ReactComponent as DeleteIcon } from '../../../../assets/images/delete.svg';
import { Link } from 'lucide-react';
import dragIIcon from '../../../../assets/images/drag-icon.svg';
import styles from "../template.module.scss";
import folderStyles from "../../Folders/Folder.module.scss";

const FolderView = ({
  folders,
  expandedFolders,
  onToggleExpansion,
  onContentItemPreview,
  onDragEnd,
  onRemoveFolder,
  onAssignFolder,
  showDragHandle = false,
  showAssignButton = false,
  className = "",
  openMarginLeft = 0,
  // New props for selection
  selectedFolder,
  onFolderSelect,
  showSelection = false
}) => {
  const getPermissionClass = (permissions) => {
    if (!permissions) return 'noone';

    // Handle array case
    if (Array.isArray(permissions)) {
      if (permissions.length === 0) return 'noone';
      permissions = permissions[0]; // Take first element
    }

    // Ensure it's a string
    const permissionStr = String(permissions);

    // Convert to lowercase and remove spaces for CSS class
    return permissionStr.toLowerCase().replace(/\s+/g, '');
  };

  const getPermissionDisplay = (permissions) => {
    if (!permissions) return 'No One';

    // Handle array case
    if (Array.isArray(permissions)) {
      if (permissions.length === 0) return 'No One';
      permissions = permissions[0]; // Take first element
    }

    // Ensure it's a string and return as is
    return String(permissions);
  };

  const renderFolder = (folder, index) => {
    const isSelected = showSelection && selectedFolder === folder._id;
    
    const folderContent = (
      <div className={styles.folderWithCheckbox}>
        {/* Assign button on the left */}
        {showAssignButton && onAssignFolder && (
          <div 
            className={styles.assignButtonLeft}
            onClick={(e) => {
              e.stopPropagation();
              onAssignFolder(folder._id, folder.title);
            }}
            title="Assign folder to tab"
          >
            <Link className={styles.assignIcon} size={16} />
          </div>
        )}
        {/* Checkbox for selection */}
        {showSelection && (
          <div 
            className={`${styles.folderCheckbox} ${isSelected ? styles.selected : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              if (onFolderSelect) {
                onFolderSelect(folder._id);
              }
            }}
          >
            {isSelected && (
              <Check className={styles.checkmarkIcon} size={10} />
            )}
          </div>
        )}
        <div 
          className={`${folderStyles.folder} ${isSelected ? styles.selectedFolder : ''}`} 
          onClick={(e) => {
            e.stopPropagation();
            if (showSelection && onFolderSelect) {
              onFolderSelect(folder._id);
            } else {
              onToggleExpansion(folder._id);
            }
          }}
        >
          <div className={folderStyles.left}>
          <div className={folderStyles.folderContent}>
            <img src={folderIcon} alt="folder" className={folderStyles.folderIcon} />
            <div className={folderStyles.folderInfo}>
              <div className={folderStyles.folderTitleWrapper}>
                <div className={folderStyles.folderTitle}>{folder.title}</div>
                <div className={`${folderStyles.type} ${folderStyles[getPermissionClass(folder.permissions)]}`}>
                  {getPermissionDisplay(folder.permissions)}
                </div>
              </div>
              <div className={folderStyles.folderItems}>
                <div className={folderStyles.items}>{folder.contentItems?.length || 0} Items</div>
                <div className={`${folderStyles.description} ${!folder.description ? folderStyles.noDescription : ''}`}>
                  {folder.description || 'No description'}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`${folderStyles.status} ${folder.status === 'Active' ? folderStyles.active : folderStyles.suspended}`}>
          {folder.status}
        </div>
        <div className={folderStyles.right} style={{ marginLeft: 0 }}>
          <div className={styles.folderActions}>
            {onRemoveFolder && (
              <div 
                className={styles.removeButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFolder(folder._id, folder.title);
                }}
                title="Remove folder from template"
              >
                <DeleteIcon className={styles.deleteIcon} />
              </div>
            )}
            <div 
              className={styles.expandButton}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpansion(folder._id);
              }}
            >
              <ChevronRight className={`${styles.chevronIcon} ${expandedFolders.has(folder._id) ? styles.expanded : ''}`} />
            </div>
          </div>
        </div>
        </div>
      </div>
    );

    const contentSection = expandedFolders.has(folder._id) && (
      <div className={`${styles.folderContentItems} ${expandedFolders.has(folder._id) ? styles.expanded : ''}`} style={{ marginLeft: openMarginLeft }}>
        <div className={styles.contentItemsHeader}>
          <span className={styles.contentItemsTitle}>Content Items ({folder.contentItems?.length || 0})</span>
        </div>
        <div className={styles.contentItemsList}>
          {folder.contentItems && folder.contentItems.length > 0 ? (
            folder.contentItems.map((item, itemIndex) => (
              <div key={`${item._id}-${itemIndex}`} className={styles.contentItemRow}>
                <FileIcon className={styles.contentItemIcon} />
                <span className={styles.contentItemTitle}>{item.title}</span>
                <DownloadIcon 
                  className={styles.previewIcon} 
                  onClick={(e) => onContentItemPreview(e, item._id)} 
                  title="Open document"
                />
              </div>
            ))
          ) : (
            <div className={styles.noContentItems}>
              <span>No content items in this folder</span>
            </div>
          )}
        </div>
      </div>
    );

    if (showDragHandle) {
      return (
        <Draggable key={folder._id} draggableId={folder._id} index={index}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              className={`${styles.folderContainer} ${snapshot.isDragging ? styles.dragging : ''}`}
            >
              <div className={styles.folderWithDrag}>
                <div {...provided.dragHandleProps} className={styles.dragHandle}>
                  <img src={dragIIcon} alt="Drag Handle" />
                </div>
                {folderContent}
              </div>
              {contentSection}
            </div>
          )}
        </Draggable>
      );
    }

    return (
      <div key={folder._id} className={styles.folderContainer}>
        {folderContent}
        {contentSection}
      </div>
    );
  };

  if (showDragHandle) {
    return (
      <DragDropContext onDragEnd={onDragEnd}>
        <div className={`${folderStyles.foldersContainer} ${className}`}>
          <Droppable droppableId="folders">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className={`${styles.droppableArea} ${snapshot.isDraggingOver ? styles.draggingOver : ''}`}
              >
                {folders.map((folder, index) => renderFolder(folder, index))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    );
  }

  return (
    <div className={`${folderStyles.foldersContainer} ${className}`}>
      {folders.map((folder, index) => renderFolder(folder, index))}
    </div>
  );
};

export default FolderView;
